import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check, Info } from "lucide-react";
import { Reveal } from "./Reveal";

const BLOCKS = [
  {
    id: "faxina-residencial", value: "residencial", num: "02", dark: false, img: "/img/diarista.jpg",
    kicker: "Serviço 02 · Diarista e faxina residencial",
    title: "Faxina Residencial e Limpeza Pesada",
    sub: "Cuidamos da limpeza da sua casa ou apartamento para você aproveitar um ambiente limpo, organizado e agradável.",
    from: "Limpeza pesada residencial", price: "R$ 350,00", unit: "",
    note: "Valor para uma limpeza pesada em residência ocupada, sujeito à avaliação das condições do imóvel e do escopo do serviço. Não é um preço único para qualquer tamanho de imóvel ou tipo de serviço.",
    listTitle: "Possibilidades de limpeza, conforme combinado",
    items: [
      "Limpeza de quartos, salas e demais ambientes", "Limpeza de pisos e superfícies",
      "Remoção de pó dos móveis e limpeza de portas", "Limpeza de cozinha e áreas de uso comum",
      "Limpeza de banheiros", "Limpeza externa de armários e eletrodomésticos",
      "Limpeza interna de geladeira e armários, mediante combinação prévia", "Lavagem e organização de roupas, quando previamente contratadas",
    ],
    extra: "Tarefas adicionais — como lavar roupas, limpar o interior de armários ou da geladeira e serviços que demandem mais tempo — precisam ser combinadas antecipadamente.",
    cta: "Solicitar faxina residencial",
  },
  {
    id: "limpeza-pos-obra", value: "pos_obra", num: "03", dark: true, img: "/img/posobra.jpg",
    kicker: "Serviço 03 · Pós-obra",
    title: "Limpeza Pós-Obra Residencial e Comercial",
    sub: "Seu imóvel pronto para a próxima etapa, com uma limpeza adequada após reformas e obras. Atendemos casas, apartamentos, salas comerciais e outros imóveis, com avaliação das condições do ambiente.",
    from: "A partir de", price: "R$ 12,00", unit: "por m²",
    note: "O valor final depende da metragem, do estado do imóvel, da quantidade de resíduos, dos acabamentos e do escopo contratado. O preço por m² é uma referência; o orçamento definitivo é apresentado após avaliação das informações e, quando necessário, visita ao local.",
    listTitle: "O serviço pode incluir",
    items: [
      "Remoção de poeira de obra e resíduos leves", "Limpeza de pisos e superfícies compatíveis com os produtos utilizados",
      "Limpeza de portas, batentes e rodapés", "Limpeza externa de armários e bancadas",
      "Limpeza de vidros acessíveis, conforme avaliação", "Limpeza de banheiros, cozinha e demais ambientes",
      "Remoção de sujeiras remanescentes da obra, conforme a viabilidade e o material das superfícies",
    ],
    extra: "Tinta, cimento, cola e outros resíduos podem exigir produtos, técnicas ou equipamentos específicos. A remoção desses materiais depende de avaliação prévia.",
    cta: "Solicitar orçamento pós-obra",
  },
];

const Block = ({ s, onPick }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const fg = s.dark ? "text-[#FAF8F5]" : "text-[#121518]";
  const muted = s.dark ? "text-[#FAF8F5]/80" : "text-[#525860]";
  const line = s.dark ? "border-[#FAF8F5]/15" : "border-[#121518]/10";
  return (
    <section ref={ref} id={s.id} data-testid={`section-${s.id}`} className={`relative overflow-hidden grain ${s.dark ? "bg-[#121518]" : "bg-[#F3F0EA]"}`}>
      <div className={`grid lg:grid-cols-12 ${s.dark ? "" : "lg:[direction:rtl]"}`}>
        <div className="lg:col-span-5 relative h-[300px] sm:h-[420px] lg:h-auto lg:min-h-[760px] overflow-hidden [direction:ltr]">
          <motion.img style={{ y, scale: 1.2 }} src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
          <span className="absolute top-5 left-5 font-mono-d text-[11px] tracking-[0.2em] bg-[#FAF8F5] text-[#121518] px-3 py-1.5">{s.kicker.toUpperCase()}</span>
        </div>
        <div className="lg:col-span-7 p-7 sm:p-12 lg:p-16 xl:p-20 [direction:ltr] flex flex-col gap-10">
          <Reveal>
            <div className="flex items-start justify-between gap-6">
              <h2 className={`font-serif-d text-4xl sm:text-5xl lg:text-6xl font-medium leading-[0.98] tracking-tight ${fg}`}>{s.title}</h2>
              <span className="font-serif-d italic text-6xl md:text-8xl text-[#C59B27] leading-none">{s.num}</span>
            </div>
            <p className={`mt-5 text-base md:text-lg max-w-xl ${muted}`}>{s.sub}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className={`border-y py-8 ${line}`}>
              <span className="font-mono-d text-[11px] tracking-[0.25em] text-[#C59B27] uppercase">{s.from}</span>
              <div className="flex items-baseline gap-3 flex-wrap" data-testid={`price-${s.id}`}>
                <span className={`font-serif-d text-6xl sm:text-7xl lg:text-8xl font-semibold tracking-tight leading-none ${fg}`}>{s.price}</span>
                {s.unit && <span className={`text-lg ${muted}`}>{s.unit}</span>}
              </div>
              <p className={`mt-5 text-sm md:text-base max-w-xl font-medium ${s.dark ? "text-[#FAF8F5]" : "text-[#121518]"}`} data-testid={`price-note-${s.id}`}>{s.note}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className={`font-mono-d text-[11px] tracking-[0.25em] uppercase mb-5 ${s.dark ? "text-[#C59B27]" : "text-[#0F4C3A]"}`}>{s.listTitle}</h3>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {s.items.map((it) => (
                <li key={it} className={`flex gap-3 text-sm md:text-base ${fg}`}><Check size={18} className="text-[#C59B27] shrink-0 mt-0.5" /> {it}</li>
              ))}
            </ul>
            <div className={`mt-8 flex gap-3 text-sm p-5 ${s.dark ? "bg-[#FAF8F5]/[0.06] text-[#FAF8F5]/85" : "bg-white text-[#525860]"}`}>
              <Info size={18} className="text-[#C59B27] shrink-0 mt-0.5" /><p>{s.extra}</p>
            </div>
            <button data-testid={`cta-${s.id}`} onClick={() => onPick(s.value)}
              className={`mt-10 rounded-full px-8 py-4 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 ${s.dark ? "bg-[#C59B27] text-[#121518] hover:bg-[#FAF8F5]" : "bg-[#0F4C3A] text-[#FAF8F5] hover:bg-[#121518]"}`}>
              {s.cta} →
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const ServiceBlocks = ({ onPick }) => (
  <div className="flex flex-col">
    {BLOCKS.map((s) => <Block key={s.id} s={s} onPick={onPick} />)}
  </div>
);
